import {
    AppBar,
    Box,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, {ReactNode, useEffect, useState} from "react";
import {ParameterDescription} from "../../fstn/action";
import { getStorage, listAll, ref, StorageReference, uploadBytesResumable } from "firebase/storage";
import {ArrowBack, Backup, Folder} from "@mui/icons-material";

const useStyles = makeStyles(theme => ({
    contents: {
        display: "flex",
        flexDirection:"column",
        border: 1,
        borderStyle: "solid",
        borderColor: theme.palette.common.black,
        borderRadius: theme.spacing(1),
        overflow: "hidden"
    },
    input: {
        margin: theme.spacing(1)
    },
    fileSelect: {
    },
    list: {
        height: 200,
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    toolbar: {
        position: "absolute",
        right: theme.spacing(4),
        top: theme.spacing(4),
        display: "flex",
        justifyContent: "right",
        padding: theme.spacing(2),
        zIndex: 5
    }
}));

interface Props {
    children: ReactNode
    name: string
    onChangeValue: (key: string, value: string | undefined) => void
    parameter: ParameterDescription
    source: string
    upload: string
    value: string
}

export default function SelectMarshalStorage({children, name, onChangeValue, parameter, source, upload, value}: Props) {
    const classes = useStyles();
    const [updating, setUpdating] = useState<string | undefined>(value);
    const [contents, setContents] = useState<StorageReference []>([]);
    const [prefixes, setPrefixes] = useState<StorageReference []>([]);
    const [progress, setProgress] = useState(0);
    const [updateTime, setUpdateTime] = useState<number>(Date.now());
    const splitValue = value.split('/');
    const [folderStack, setFolderStack] = useState<string []>(splitValue.slice(0, splitValue.length - 1));
    const handleValueChange = ({currentTarget}: React.ChangeEvent<HTMLInputElement>) => {
        setUpdating(currentTarget.value);
    };
    const handleInputBlur = () => {
        onChangeValue(name, updating);
    }
    const handleContentsClick = (event: React.MouseEvent<HTMLElement>) => {
        const selected = [...folderStack, event.currentTarget.dataset.source].join('/');

        setUpdating(selected);
        onChangeValue(name, selected);
    };
    const handlePrefixClick = (event: React.MouseEvent<HTMLElement>) => {
        setFolderStack([...folderStack, event.currentTarget.dataset.source as string]);
    };
    const handleBackClick = () => {
        setFolderStack(folderStack.slice(0, folderStack.length - 1));
    };
    const handleUpload = ({target}: React.ChangeEvent<HTMLInputElement>) => {
        if (target.files !== null && target.files.length > 0) {
            const file = target.files[0];
            const storageRef = ref(getStorage(), [source, ...folderStack, file.name].join('/'));
            const task = uploadBytesResumable(storageRef, file, { contentType: upload});
            const progressPerByte = 100 / file.size;

            setProgress(0);
            task.on('state_changed', snapshot => {
                setProgress(snapshot.bytesTransferred * progressPerByte);
            }, error => console.error(error.code),
            () => {
                setProgress(100);
                setUpdateTime(Date.now());
                setTimeout(() => setProgress(0), 2000);
            });
        }
    };

    useEffect(() => {
        const storageRef = ref(getStorage(), [source, ...folderStack].join('/'));

        listAll(storageRef).then(results => {
            setPrefixes(results.prefixes);
            setContents(results.items);
        });
    }, [folderStack, source, setContents, updateTime]);
    return (
        <Box className={classes.contents}>
            {children}
            <Box className={classes.fileSelect}>
                <AppBar position="static">
                    <Toolbar variant="dense">
                        <Box flexGrow={1} m={1}>
                            {progress > 0 && < LinearProgress color="primary" variant="determinate" value={progress}/>}
                        </Box>
                        <input hidden accept={upload} id="upload-resource" type="file" onChange={handleUpload}/>
                        <label htmlFor="upload-resource">
                            <IconButton color="inherit" component="span" edge="start" size="small">
                                <Backup/>
                            </IconButton>
                        </label>
                        <IconButton
                            color="inherit" disabled={folderStack.length === 0} edge="end" onClick={handleBackClick}
                            size="small">
                            <ArrowBack/>
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <List className={classes.list} dense>
                    {prefixes.map(reference => (
                        <ListItem button data-source={reference.name} key={reference.name} onClick={handlePrefixClick}>
                            <ListItemIcon><Folder/></ListItemIcon>
                            <Typography variant="caption">{reference.name}</Typography>
                        </ListItem>
                    ))}
                    {contents.map(reference => (
                        <ListItem button data-source={reference.name} key={reference.name} onClick={handleContentsClick}>
                            <Typography variant="caption">{reference.name}</Typography>
                        </ListItem>
                    ))}
                </List>
            </Box>
            <Box className={classes.input}>
                <TextField title={parameter.description} fullWidth label={name} value={updating}
                           onBlur={handleInputBlur} onChange={handleValueChange}/>
                </Box>
        </Box>
    );
}